import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Gantt } from "dhtmlx-gantt";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import "./OssDashboardTimeline.css";
import "./OssLifecycleGantt.css";
import { orderStatus } from "../../constants/labels";

const EMPTY_ORDERS = [];
const escapeHtml = (value) => String(value ?? "-").replace(/[&<>"']/g,
  (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
const day = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};
const addDays = (date, count) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
const shortDate = (value) => value ? String(value).slice(2, 10) : "미정";
const stageColors = { RECEIVED: "#2563eb", CHECKING: "#a16207", CONFIRMED: "#0f766e", IN_PROGRESS: "#6366f1", HOLD: "#b45309" };
const stageRank = { RECEIVED: 1, CHECKING: 2, CONFIRMED: 3, IN_PROGRESS: 4, HOLD: 5 };
const eventColor = (event) => ["POSTPONE", "CHANGE", "REJECT"].includes(event.event_type)
  ? "#c2410c" : stageColors[event.after_status] || "#475569";
const zooms = [{ key: "fit", label: "전체 기간" }, { key: "year", label: "연" },
  { key: "month", label: "월" }, { key: "week", label: "주" }, { key: "day", label: "일" }];

function buildTasks(orders, today) {
  return orders.map((order) => {
    const milestones = (order.milestones || []).map((event) => ({ ...event, date: day(event.occurred_at) }))
      .filter((event) => event.date).sort((a, b) => a.date - b.date || String(a.occurred_at).localeCompare(String(b.occurred_at)));
    const orderDate = day(order.order_date);
    const registered = day(order.registered_date);
    const start = new Date(Math.min(Number(orderDate || registered || today), ...milestones.map((e) => Number(e.date))));
    const due = day(order.last_due);
    const end = addDays(new Date(Math.max(Number(start), Number(today), Number(due || start), ...milestones.map((e) => Number(e.date)))), 1);
    const anchors = [...milestones];
    if (orderDate || registered) anchors.unshift({ id: "order-date", date: orderDate || registered,
      label: orderDate ? "주문일" : "시스템 등록", event_type: "ORDER_DATE", occurred_at: order.order_date || order.registered_date });
    if (due) anchors.push({ id: "due-date", date: due, label: "요청 납기", event_type: "DUE", occurred_at: order.last_due });
    const groups = new Map();
    anchors.sort((a, b) => a.date - b.date).forEach((event) => {
      const key = event.date.getTime();
      if (!groups.has(key)) groups.set(key, { date: event.date, events: [] });
      groups.get(key).events.push(event);
    });
    return { ...order, id: String(order.order_id), orderId: String(order.order_id), text: order.order_no,
      start_date: start, end_date: end, milestones, anchors: [...groups.values()], due, today };
  });
}

function columns(sort) {
  const header = (key, label) => `<button type="button" class="oss-life-sort" data-sort="${key}" aria-label="${label} 정렬">${label}${sort.key === key ? (sort.direction === 1 ? " ↑" : " ↓") : ""}</button>`;
  return [
    { name: "order", label: header("order_no", "주문 / 거래처"), width: 186, align: "center",
      template: (task) => `<button type="button" class="oss-life-order-link" data-order-start="${escapeHtml(task.orderId)}" aria-label="${escapeHtml(task.order_no)} 시작으로 이동" title="${escapeHtml(task.order_no)} · ${escapeHtml(task.customer_name)} · 시작으로 이동"><strong>${escapeHtml(task.order_no)}</strong><span>${escapeHtml(task.customer_name)}</span></button>` },
    { name: "stage", label: header("order_status", "현재 단계"), width: 82, align: "center",
      template: (task) => `<span class="oss-life-stage-chip" style="--stage-color:${stageColors[task.order_status] || "#475569"}">${escapeHtml(orderStatus[task.order_status] || task.order_status)}</span>` },
    { name: "received", label: header("order_date", "주문일"), width: 76, align: "center", template: (task) => escapeHtml(shortDate(task.order_date)) },
    { name: "due", label: header("last_due", "최종 납기"), width: 76, align: "center", template: (task) => escapeHtml(shortDate(task.last_due)) },
  ];
}

function configureScale(gantt, container, tasks, zoom, today) {
  const dates = [today, ...tasks.flatMap((task) => [task.start_date, task.end_date])];
  const min = new Date(Math.min(...dates.map(Number)));
  const max = new Date(Math.max(...dates.map(Number)));
  gantt.config.start_date = new Date(min.getFullYear(), min.getMonth(), 1);
  gantt.config.end_date = new Date(max.getFullYear(), max.getMonth() + 1, 1);
  gantt.config.grid_width = container.clientWidth < 640 ? 268 : 420;
  gantt.config.columns = gantt.config.columns.map((column) => ({ ...column,
    hide: container.clientWidth < 640 && ["received", "due"].includes(column.name) }));
  gantt.config.scale_height = 52;
  const months = Math.max(1, (max.getFullYear() - min.getFullYear()) * 12 + max.getMonth() - min.getMonth() + 1);
  if (zoom === "fit" || zoom === "month") {
    gantt.config.scales = [{ unit: "year", step: 1, format: "%Y년" }, { unit: "month", step: 1, format: "%n월" }];
    gantt.config.min_column_width = zoom === "fit" ? Math.max(24, Math.floor((container.clientWidth - gantt.config.grid_width - 18) / months)) : 96;
  } else if (zoom === "year") {
    gantt.config.scales = [{ unit: "year", step: 1, format: "%Y년" }];
    gantt.config.min_column_width = 180;
  } else {
    gantt.config.scales = [{ unit: zoom === "week" ? "week" : "month", step: 1, format: zoom === "week" ? "%m/%d 주" : "%Y년 %m월" },
      { unit: "day", step: 1, format: "%d" }];
    gantt.config.min_column_width = zoom === "week" ? 34 : 72;
  }
  gantt.config.show_task_cells = ["week", "day"].includes(zoom);
}

function barMarkup(gantt, task) {
  const left = gantt.posFromDate(task.start_date);
  const width = Math.max(1, gantt.posFromDate(task.end_date) - left);
  const position = (date) => Math.min(100, Math.max(0, ((gantt.posFromDate(date) - left) / width) * 100));
  return `<span class="oss-life-elapsed" style="width:${position(task.today)}%"></span>` + task.anchors.map((group, index) => {
    const at = position(group.date);
    const next = task.anchors[index + 1];
    const available = (next ? position(next.date) : 100) - at;
    const first = group.events[0];
    const label = group.events.map((e) => e.label).join(" · ");
    const caption = `${first.label}${group.events.length > 1 ? ` +${group.events.length - 1}` : ""}`;
    return `<span class="oss-life-milestone ${first.event_type === "DUE" ? "is-due" : ""}" style="left:${at}%;--event-color:${eventColor(first)}" title="${escapeHtml(first.occurred_at?.slice(0, 10))} · ${escapeHtml(label)}"></span>` +
      (available * width / 100 > 65 ? `<span class="oss-life-milestone-label" style="left:${at}%;width:${available}%">${escapeHtml(caption)}</span>` : "");
  }).join("");
}

function tooltip(task) {
  const history = task.milestones.slice(-6).reverse();
  return `<div class="oss-gantt-tooltip oss-life-tooltip"><div class="oss-gantt-tooltip-title">${escapeHtml(task.order_no)} · ${escapeHtml(task.customer_name)}</div>
    <div class="oss-gantt-tooltip-grid"><span>현재 단계</span><strong>${escapeHtml(orderStatus[task.order_status])}</strong>
    <span>고객 발주번호</span><strong>${escapeHtml(task.customer_po_no || "미등록")}</strong>
    <span>주문일</span><strong>${escapeHtml(task.order_date || "미기록")}</strong>
    <span>요청 납기</span><strong>${escapeHtml(task.first_due || "미정")}${task.first_due !== task.last_due ? ` ~ ${escapeHtml(task.last_due)}` : ""}</strong>
    <span>품목</span><strong>${task.item_count}건</strong></div>
    <div class="oss-life-tooltip-history"><strong>주요 이력 ${task.milestones.length}건</strong>${history.length ? history.map((e) =>
      `<div><span>${escapeHtml(e.occurred_at?.slice(0, 16))}</span><b>${escapeHtml(e.label)}</b></div>`).join("") : "<p>등록된 단계·변경 이력이 없습니다.</p>"}
    ${task.milestones.length > 6 ? `<p>최근 6건 / 전체 ${task.milestones.length}건</p>` : ""}</div></div>`;
}

export const OssDashboardGanttView = ({ orders = EMPTY_ORDERS, businessDate, stageFilter = "total", loading, onOpenOrder }) => {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);
  const currentRef = useRef({});
  const pendingFocusRef = useRef(null);
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState("fit");
  const [sort, setSort] = useState({ key: "order_date", direction: 1 });
  const today = useMemo(() => day(businessDate) || day(new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Seoul" }).format(new Date())), [businessDate]);
  const tasks = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return buildTasks(orders.filter((order) => (stageFilter === "total" || order.order_status === stageFilter) &&
      [order.order_no, order.customer_name, order.customer_po_no].some((value) => String(value || "").toLocaleLowerCase().includes(query))), today)
      .sort((a, b) => {
        const comparison = sort.key === "order_status" ? (stageRank[a.order_status] || 0) - (stageRank[b.order_status] || 0)
          : String(a[sort.key] || "").localeCompare(String(b[sort.key] || ""), "ko", { numeric: true });
        return comparison * sort.direction || a.orderId.localeCompare(b.orderId, "en", { numeric: true });
      });
  }, [orders, stageFilter, search, today, sort]);
  currentRef.current = { tasks, zoom, sort, today, onOpenOrder };

  useEffect(() => {
    const container = containerRef.current;
    const gantt = Gantt.getGanttInstance();
    ganttRef.current = gantt;
    gantt.plugins({ tooltip: true, marker: true });
    Object.assign(gantt.config, { readonly: true, drag_move: false, drag_resize: false, drag_progress: false,
      show_links: false, autosize: false, autoscroll: false, smart_rendering: true, smart_scales: true,
      scroll_size: 14, row_height: 58, bar_height: 32, show_progress: false, show_grid: true });
    gantt.config.columns = columns(currentRef.current.sort);
    gantt.templates.task_class = () => "oss-life-order-bar";
    gantt.templates.task_text = (_start, _end, task) => barMarkup(gantt, task);
    gantt.templates.tooltip_text = (_start, _end, task) => tooltip(task);
    const weekend = (date) => ["week", "day"].includes(currentRef.current.zoom) && [0, 6].includes(date.getDay());
    gantt.templates.scale_cell_class = (date) => weekend(date) ? "oss-gantt-holiday-scale" : "";
    gantt.templates.timeline_cell_class = (_task, date) => weekend(date) ? "oss-gantt-holiday-cell" : "";
    const focusOrderStart = (id) => {
      if (!gantt.isTaskExists(id)) return;
      gantt.ext.tooltips?.tooltip?.hide();
      const { y } = gantt.getScrollState();
      // Expand overview scales before positioning so the first milestones are readable.
      if (["fit", "year"].includes(currentRef.current.zoom)) {
        pendingFocusRef.current = { id, y };
        setZoom("month");
      } else {
        gantt.selectTask(id);
        gantt.scrollTo(Math.max(0, gantt.posFromDate(gantt.getTask(id).start_date) - 32), y);
      }
    };
    const clickId = gantt.attachEvent("onTaskClick", (id, event) => {
      gantt.ext.tooltips?.tooltip?.hide();
      if (event?.target?.closest?.(".gantt_grid")) focusOrderStart(id);
      else currentRef.current.onOpenOrder?.(gantt.getTask(id).orderId);
      return false;
    });
    const doubleClickId = gantt.attachEvent("onTaskDblClick", () => false);
    const headerClick = (event) => {
      const header = event.target.closest?.("[data-sort]");
      if (header) setSort((value) => ({ key: header.dataset.sort, direction: value.key === header.dataset.sort ? -value.direction : 1 }));
    };
    const keyOpen = (event) => {
      const button = event.target.closest?.("[data-order-start]");
      if (button && ["Enter", " "].includes(event.key)) {
        event.preventDefault();
        focusOrderStart(button.dataset.orderStart);
      }
    };
    container.addEventListener("click", headerClick);
    container.addEventListener("keydown", keyOpen);
    configureScale(gantt, container, currentRef.current.tasks, currentRef.current.zoom, currentRef.current.today);
    gantt.init(container);
    let frame;
    let previousSize = "";
    const observer = new ResizeObserver(() => {
      const size = `${container.clientWidth}:${container.clientHeight}`;
      if (size === previousSize) return;
      previousSize = size;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const current = currentRef.current;
        configureScale(gantt, container, current.tasks, current.zoom, current.today);
        gantt.render();
        gantt.setSizes();
      });
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      container.removeEventListener("click", headerClick);
      container.removeEventListener("keydown", keyOpen);
      gantt.detachEvent(clickId);
      gantt.detachEvent(doubleClickId);
      gantt.destructor();
      ganttRef.current = null;
    };
  }, []);

  const previousZoom = useRef(zoom);
  useEffect(() => {
    const gantt = ganttRef.current;
    if (!gantt) return;
    const scroll = gantt.getScrollState();
    gantt.config.columns = columns(sort);
    configureScale(gantt, containerRef.current, tasks, zoom, today);
    gantt.clearAll();
    gantt.parse({ data: tasks, links: [] });
    gantt.addMarker({ start_date: today, css: "oss-life-today", text: "오늘", title: businessDate || "오늘" });
    gantt.render();
    const pendingFocus = pendingFocusRef.current;
    pendingFocusRef.current = null;
    if (pendingFocus && gantt.isTaskExists(pendingFocus.id)) {
      gantt.selectTask(pendingFocus.id);
      gantt.scrollTo(Math.max(0, gantt.posFromDate(gantt.getTask(pendingFocus.id).start_date) - 32), pendingFocus.y);
    } else if (previousZoom.current !== zoom) {
      if (zoom === "fit") gantt.scrollTo(0, scroll.y);
      else gantt.showDate(addDays(today, zoom === "month" ? -30 : -3));
    } else gantt.scrollTo(scroll.x, scroll.y);
    previousZoom.current = zoom;
  }, [tasks, zoom, sort, today, businessDate]);

  return <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}>
    <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" gap={1}>
      <TextField size="small" placeholder="주문번호, 고객 발주번호, 거래처 검색" value={search} onChange={(e) => setSearch(e.target.value)}
        inputProps={{ "aria-label": "생애주기 주문 검색" }} sx={{ width: { xs: "100%", md: 310 } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
      <Typography sx={{ fontSize: 12, color: "#64748b", mr: "auto" }}>{stageFilter === "total" ? "전체" : orderStatus[stageFilter]} {tasks.length}건</Typography>
      <Tooltip title="오늘로 이동"><IconButton aria-label="오늘로 이동" onClick={() => ganttRef.current?.showDate(addDays(today, -3))}><TodayOutlinedIcon fontSize="small" /></IconButton></Tooltip>
      <ToggleButtonGroup exclusive size="small" value={zoom} onChange={(_event, value) => value && setZoom(value)} aria-label="간트 기간 단위">
        {zooms.map((option) => <ToggleButton key={option.key} value={option.key} sx={{ px: 1.4, py: 0.6 }}>{option.label}</ToggleButton>)}
      </ToggleButtonGroup>
    </Stack>
    <Box sx={{ flex: 1, position: "relative", minHeight: 0, overflow: "hidden" }}>
      <Box ref={containerRef} className="oss-lifecycle-gantt" />
      {!tasks.length && !loading && <Box sx={{ position: "absolute", top: 70, left: 0, right: 0, textAlign: "center", py: 5, bgcolor: "white", color: "#64748b" }}>
        {search || stageFilter !== "total" ? "조건에 맞는 주문이 없습니다." : "진행 중인 주문이 없습니다."}
      </Box>}
      {loading && <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.7)" }}><CircularProgress size={28} /></Box>}
    </Box>
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={2} className="oss-life-gantt-legend">
      <span><i className="is-record" />주문·단계 이력</span><span><i className="is-change" />변경·연기 요청</span>
      <span><i className="is-due" />요청 납기</span>{["week", "day"].includes(zoom) && <span><i className="is-weekend" />주말</span>}
    </Stack>
  </Stack>;
};
